/**
 * Bare-Metal Order Status Page
 * Tracks order status from payment to provisioning
 */

import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  CheckCircleIcon,
  ClockIcon,
  ServerIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import Header from '../../components/layout/Header';
import { baremetalApi } from '../../services/baremetalApi';

const OrderStatus: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();

  const selectedProjectId = localStorage.getItem('selectedProjectId') || '';

  // Fetch order status with auto-refresh
  const { data: orderStatus, isLoading, refetch } = useQuery({
    queryKey: ['baremetal-order-status', orderId],
    queryFn: () => baremetalApi.getOrderStatus(orderId!, selectedProjectId),
    enabled: !!orderId,
    refetchInterval: (query) => {
      // Stop polling if order is active or cancelled
      const status = query.state.data?.data?.status;
      if (status === 'active' || status === 'cancelled') {
        return false;
      }
      // Poll every 5 seconds for pending/paid/provisioning
      return 5000;
    },
  });

  const order = orderStatus?.data;

  // Redirect to instances if provisioned
  useEffect(() => {
    if (order?.status === 'active') {
      setTimeout(() => {
        navigate(`/portal/baremetal/instances/${orderId}`);
      }, 2000);
    }
  }, [order?.status, orderId, navigate]);

  const getStatusStep = () => {
    switch (order?.status) {
      case 'pending':
        return 1;
      case 'paid':
        return 2;
      case 'provisioning':
        return 3;
      case 'active':
        return 4;
      case 'cancelled':
        return 0;
      default:
        return 1;
    }
  };

  const currentStep = getStatusStep();

  const statusMessages: Record<string, { title: string; message: string; icon: React.ReactNode }> = {
    pending: {
      title: 'Payment Pending',
      message: 'Please complete the payment to proceed with your order.',
      icon: <ClockIcon className="w-12 h-12 text-yellow-400" />,
    },
    paid: {
      title: 'Payment Confirmed',
      message: 'Your payment has been confirmed. Our team has been notified to provision your server.',
      icon: <CheckCircleIcon className="w-12 h-12 text-green-400" />,
    },
    provisioning: {
      title: 'Provisioning in Progress',
      message: 'Our team is setting up your bare-metal server. This typically takes 24-48 hours.',
      icon: <ServerIcon className="w-12 h-12 text-blue-400 animate-pulse" />,
    },
    active: {
      title: 'Server Ready!',
      message: 'Your bare-metal server has been provisioned and is now active.',
      icon: <CheckCircleIcon className="w-12 h-12 text-green-400" />,
    },
    cancelled: {
      title: 'Order Cancelled',
      message: 'This order has been cancelled.',
      icon: <ExclamationTriangleIcon className="w-12 h-12 text-red-400" />,
    },
  };

  const currentStatus = order?.status || 'pending';
  const statusInfo = statusMessages[currentStatus] || statusMessages.pending;

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-gray-700 border-t-primary-500 rounded-full animate-spin mb-4 mx-auto"></div>
          <p className="text-sm text-gray-400">Loading order status...</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <p className="text-white">Order not found</p>
          <button
            onClick={() => navigate('/portal/baremetal/create')}
            className="mt-4 text-primary-500 hover:text-primary-400"
          >
            Create New Order
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header
        title="Order Status"
        subtitle={`Tracking order ${order.order_id}`}
      />

      <div className="p-6 max-w-4xl mx-auto space-y-8">
        {/* Status Icon and Message */}
        <div className="bg-gray-900 rounded-xl border border-gray-700 p-8 text-center">
          <div className="flex justify-center mb-4">
            {statusInfo.icon}
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">{statusInfo.title}</h2>
          <p className="text-gray-400">{statusInfo.message}</p>

          {order.status_message && (
            <div className="mt-4 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <p className="text-sm text-blue-400">{order.status_message}</p>
            </div>
          )}
        </div>

        {/* Timeline */}
        {currentStatus !== 'cancelled' && (
          <div className="bg-gray-900 rounded-xl border border-gray-700 p-8">
            <h3 className="text-lg font-semibold text-white mb-6">Order Progress</h3>

            <div className="space-y-6">
              {/* Step 1: Order Created */}
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      currentStep >= 1 ? 'bg-green-500' : 'bg-gray-700'
                    }`}
                  >
                    <CheckCircleIcon className="w-6 h-6 text-white" />
                  </div>
                </div>
                <div className="flex-1 pt-1">
                  <p className={`font-medium ${currentStep >= 1 ? 'text-white' : 'text-gray-400'}`}>
                    Order Created
                  </p>
                  <p className="text-sm text-gray-500">Your order has been placed</p>
                </div>
              </div>

              {/* Connector */}
              <div className="ml-5">
                <div className={`w-0.5 h-8 ${currentStep >= 2 ? 'bg-green-500' : 'bg-gray-700'}`} />
              </div>

              {/* Step 2: Payment Confirmed */}
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      currentStep >= 2 ? 'bg-green-500' : currentStep === 1 ? 'bg-yellow-500 animate-pulse' : 'bg-gray-700'
                    }`}
                  >
                    {currentStep >= 2 ? (
                      <CheckCircleIcon className="w-6 h-6 text-white" />
                    ) : (
                      <ClockIcon className="w-6 h-6 text-white" />
                    )}
                  </div>
                </div>
                <div className="flex-1 pt-1">
                  <p className={`font-medium ${currentStep >= 2 ? 'text-white' : 'text-gray-400'}`}>
                    Payment Confirmed
                  </p>
                  <p className="text-sm text-gray-500">
                    {order.paid_at
                      ? `Paid on ${new Date(order.paid_at).toLocaleString()}`
                      : 'Awaiting payment'}
                  </p>
                </div>
              </div>

              {/* Connector */}
              <div className="ml-5">
                <div className={`w-0.5 h-8 ${currentStep >= 3 ? 'bg-green-500' : 'bg-gray-700'}`} />
              </div>

              {/* Step 3: Provisioning */}
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      currentStep >= 3 ? 'bg-blue-500' : 'bg-gray-700'
                    }`}
                  >
                    <ServerIcon className={`w-6 h-6 text-white ${currentStep === 3 ? 'animate-pulse' : ''}`} />
                  </div>
                </div>
                <div className="flex-1 pt-1">
                  <p className={`font-medium ${currentStep >= 3 ? 'text-white' : 'text-gray-400'}`}>
                    Provisioning
                  </p>
                  <p className="text-sm text-gray-500">Our team is setting up your server</p>
                </div>
              </div>

              {/* Connector */}
              <div className="ml-5">
                <div className={`w-0.5 h-8 ${currentStep >= 4 ? 'bg-green-500' : 'bg-gray-700'}`} />
              </div>

              {/* Step 4: Active */}
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      currentStep >= 4 ? 'bg-green-500' : 'bg-gray-700'
                    }`}
                  >
                    <CheckCircleIcon className="w-6 h-6 text-white" />
                  </div>
                </div>
                <div className="flex-1 pt-1">
                  <p className={`font-medium ${currentStep >= 4 ? 'text-white' : 'text-gray-400'}`}>
                    Server Active
                  </p>
                  <p className="text-sm text-gray-500">
                    {order.provisioned_at
                      ? `Provisioned on ${new Date(order.provisioned_at).toLocaleString()}`
                      : 'Server will be ready soon'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Order Details */}
        <div className="bg-gray-900 rounded-xl border border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Order Details</h3>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-400">Order ID</p>
              <p className="text-white font-mono">{order.order_id}</p>
            </div>
            <div>
              <p className="text-sm text-gray-400">Status</p>
              <p className="text-white capitalize">{order.status}</p>
            </div>
            <div>
              <p className="text-sm text-gray-400">CPU</p>
              <p className="text-white">{order.cpu} Cores</p>
            </div>
            <div>
              <p className="text-sm text-gray-400">RAM</p>
              <p className="text-white">{order.ram} GB</p>
            </div>
            <div>
              <p className="text-sm text-gray-400">Storage</p>
              <p className="text-white">{order.storage} GB {order.storage_type}</p>
            </div>
            {order.gpu && (
              <div>
                <p className="text-sm text-gray-400">GPU</p>
                <p className="text-white">{order.gpu_count}x {order.gpu}</p>
              </div>
            )}
            <div>
              <p className="text-sm text-gray-400">Duration</p>
              <p className="text-white">{order.duration} month{order.duration > 1 ? 's' : ''}</p>
            </div>
            <div>
              <p className="text-sm text-gray-400">Total Price</p>
              <p className="text-white">₹{order.total_price_inr?.toLocaleString()}</p>
            </div>
          </div>

          {order.expires_at && (
            <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
              <p className="text-sm text-yellow-400">
                <strong>Expires:</strong> {new Date(order.expires_at).toLocaleString()}
              </p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => refetch()}
            className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-sm transition-colors"
          >
            <ArrowPathIcon className="w-4 h-4" />
            Refresh Status
          </button>

          {currentStatus === 'active' && (
            <button
              onClick={() => navigate(`/portal/baremetal/instances/${orderId}`)}
              className="px-4 py-2 bg-primary-500 hover:bg-primary-600 rounded-lg text-white text-sm transition-colors"
            >
              View Instance Details
            </button>
          )}

          {currentStatus === 'pending' && (
            <button
              onClick={() => navigate(`/portal/baremetal/checkout/${orderId}`)}
              className="px-4 py-2 bg-primary-500 hover:bg-primary-600 rounded-lg text-white text-sm transition-colors"
            >
              Complete Payment
            </button>
          )}
        </div>

        {/* Support Info */}
        <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-6">
          <h4 className="text-sm font-semibold text-blue-400 mb-2">Need Help?</h4>
          <p className="text-sm text-gray-400 mb-2">
            If you have any questions about your order, please contact our support team at{' '}
            <a href="mailto:support@podstack.ai" className="text-blue-400 hover:underline">
              support@podstack.ai
            </a>
          </p>
          <p className="text-xs text-gray-500">Reference Order ID: {order.order_id}</p>
        </div>
      </div>
    </div>
  );
};

export default OrderStatus;
